import { type TypedDocumentNode, gql } from '@apollo/client';
import styled from 'styled-components';

import { PlayerGameScoreProjectionGrade } from '@sorare/core/src/__generated__/globalTypes';
import { HexagonShape } from '@sorare/core/src/atoms/icons/HexagonShape';
import { BrandS, BrandXS } from '@sorare/core/src/atoms/typography';
import { absCenter, unitMapping } from '@sorare/core/src/lib/style';

import type { GradeHexagon_playerGameScoreProjection } from './__generated__/index.graphql';

const gradeColors = {
  [PlayerGameScoreProjectionGrade.A]: 'var(--c-score-high)',
  [PlayerGameScoreProjectionGrade.B]: 'var(--c-score-mediumHigh)',
  [PlayerGameScoreProjectionGrade.C]: 'var(--c-score-medium)',
  [PlayerGameScoreProjectionGrade.D]: 'var(--c-score-mediumLow)',
  [PlayerGameScoreProjectionGrade.E]: 'var(--c-score-low)',
  [PlayerGameScoreProjectionGrade.F]: 'var(--c-score-veryLow)',
};

const SmallLabel = styled(BrandXS)`
  ${absCenter}
`;
const Label = styled(BrandS)`
  ${absCenter}
`;

const HIGH_RELIABILITY_THRESHOLD = 5000;

export const GradeHexagon = ({
  projection,
  size = 4,
}: {
  projection: GradeHexagon_playerGameScoreProjection | undefined;
  size?: keyof typeof unitMapping;
}) => {
  const { grade, reliabilityBasisPoints } = projection || {};

  const ActualLabel = size < 3 ? SmallLabel : Label;

  return (
    <div style={{ position: 'relative' }}>
      <ActualLabel color="var(--c-black)">{grade || '-'}</ActualLabel>
      <HexagonShape
        size={size}
        fill={
          (reliabilityBasisPoints || 0) > HIGH_RELIABILITY_THRESHOLD && grade
            ? gradeColors[grade]
            : 'var(--c-nd-600-opaque)'
        }
      />
    </div>
  );
};

GradeHexagon.fragments = {
  projection: gql`
    fragment GradeHexagon_playerGameScoreProjection on PlayerGameScoreProjection {
      id
      grade
      reliabilityBasisPoints
    }
  ` as TypedDocumentNode<GradeHexagon_playerGameScoreProjection>,
};
